import { create } from 'zustand';
import { format, addDays } from 'date-fns';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { parseDateOnly } from '@/lib/dateOnly';
import type { CalendarDateTimeValue, CalendarEventModel } from '@/types/calendar';
import { useCalendarActionsStore } from './calendarActionsStore';
import { useCalendarStore } from './calendarStore';

interface DraggedEventData {
  eventId: string;
  calendarUid: string;
  originalStart: CalendarDateTimeValue;
  originalEnd: CalendarDateTimeValue;
  allDay: boolean;
  durationMinutes: number;
  // Original time-of-day in minutes (for timed events)
  originalStartMinutes: number | null;
  originalEndMinutes: number | null;
  // For drag preview rendering
  summary: string | null;
  backgroundColor: string | null;
  // Column info for maintaining width (timed events)
  column: number;
  columnCount: number;
  // Offset from event start where user clicked (in minutes)
  dragOffsetMinutes: number;
}

interface DragPreview {
  date: string; // YYYY-MM-DD
  startMinutes: number | null; // null for all-day events
  endMinutes: number | null;
}

interface ResizeEventData {
  eventId: string;
  calendarUid: string;
  edge: 'start' | 'end'; // 'start' = left/top edge, 'end' = right/bottom edge
  originalStart: CalendarDateTimeValue;
  originalEnd: CalendarDateTimeValue;
  originalStartMinutes: number;
  originalEndMinutes: number;
  date: string;
  allDay: boolean;
  originalStartDate: string | null;
  originalEndDate: string | null;
  // Column info for maintaining width (timed events)
  column: number;
  columnCount: number;
  // Row index for all-day events (to position preview in same row)
  rowIndex: number;
}

interface ResizePreview {
  startMinutes: number;
  endMinutes: number;
  // For all-day events
  startDate: string | null;
  endDate: string | null;
}

interface QuickCreateState {
  open: boolean;
  date: string; // YYYY-MM-DD
  startMinutes: number | null; // null = all-day
  endMinutes: number | null;
  anchorPosition: { x: number; y: number } | null;
}

interface CalendarDragDropState {
  // Drag state
  draggedEvent: DraggedEventData | null;
  dragPreview: DragPreview | null;
  isDragging: boolean;

  // Resize state
  resizeEvent: ResizeEventData | null;
  resizePreview: ResizePreview | null;
  isResizing: boolean;

  // Quick-create state
  quickCreate: QuickCreateState;

  // Drag actions
  startEventDrag: (event: CalendarEventModel, columnInfo?: { column: number; columnCount: number }, dragOffsetMinutes?: number) => void;
  updateDragPreview: (date: string, startMinutes: number | null, endMinutes: number | null) => void;
  clearDragPreview: () => void;
  endEventDrag: () => void;
  commitEventDrag: () => Promise<boolean>;

  // Resize actions
  startEventResize: (event: CalendarEventModel, edge: 'start' | 'end', date: string, columnInfo?: { column: number; columnCount: number }, rowIndex?: number) => void;
  updateResizePreview: (preview: { startMinutes?: number; endMinutes?: number; startDate?: string; endDate?: string }) => void;
  endEventResize: () => void;
  commitEventResize: () => Promise<boolean>;

  // Quick-create actions
  openQuickCreate: (date: string, startMinutes: number | null, endMinutes: number | null, anchorPosition: { x: number; y: number } | null) => void;
  closeQuickCreate: () => void;
}

function getEventDurationMinutes(event: CalendarEventModel): number {
  if (event.allDay) {
    // For all-day events, calculate days difference
    const startDate = event.start?.date || event.startDate;
    const endDate = event.end?.date || event.endDate;
    if (!startDate || !endDate) return 24 * 60;
    const start = parseDateOnly(startDate);
    const end = parseDateOnly(endDate);
    if (!start || !end) return 24 * 60;
    // End date is exclusive for all-day events
    const days = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000)));
    return days * 24 * 60;
  }

  const startTime = event.start?.dateTime;
  const endTime = event.end?.dateTime;
  if (!startTime || !endTime) return 60;

  const startMs = new Date(startTime).getTime();
  const endMs = new Date(endTime).getTime();
  return Math.max(15, Math.round((endMs - startMs) / 60000));
}

function getMinutesFromDateTime(dateTime: string | null | undefined): number {
  if (!dateTime) return 0;
  const date = new Date(dateTime);
  return date.getHours() * 60 + date.getMinutes();
}

function buildDateTime(date: string, minutes: number): string {
  // Build a simple ISO datetime string (no timezone conversion needed here).
  // The API will interpret this time in the context of the timeZone field we provide.
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${date}T${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:00`;
}

function isSameDateTimeValue(a: CalendarDateTimeValue, b: CalendarDateTimeValue): boolean {
  // Compare date-only values
  if (a.date && b.date) {
    return a.date === b.date;
  }
  // Compare datetime values (ignore timezone differences, just compare the datetime portion)
  if (a.dateTime && b.dateTime) {
    // Extract just the datetime part (YYYY-MM-DDTHH:MM:SS)
    const aDateTime = a.dateTime.slice(0, 19);
    const bDateTime = b.dateTime.slice(0, 19);
    return aDateTime === bDateTime;
  }
  return false;
}

const INITIAL_QUICK_CREATE: QuickCreateState = {
  open: false,
  date: '',
  startMinutes: null,
  endMinutes: null,
  anchorPosition: null,
};

export const useCalendarDragDropStore = create<CalendarDragDropState>()(
  conditionalDevtools(
    (set, get) => ({
      // Initial state
      draggedEvent: null,
      dragPreview: null,
      isDragging: false,
      resizeEvent: null,
      resizePreview: null,
      isResizing: false,
      quickCreate: { ...INITIAL_QUICK_CREATE },

      // Drag actions
      startEventDrag: (event, columnInfo, dragOffsetMinutes) => {
        const durationMinutes = getEventDurationMinutes(event);
        const originalStartMinutes = event.allDay ? null : getMinutesFromDateTime(event.start?.dateTime);
        const originalEndMinutes = event.allDay ? null : getMinutesFromDateTime(event.end?.dateTime);
        // Get background color from event or calendar
        const calendar = useCalendarStore.getState().calendars[event.calendarUid];
        const backgroundColor = event.color?.backgroundColor ?? calendar?.backgroundColor ?? null;
        set({
          draggedEvent: {
            eventId: event.id,
            calendarUid: event.calendarUid,
            originalStart: event.start,
            originalEnd: event.end,
            allDay: event.allDay,
            durationMinutes,
            originalStartMinutes,
            originalEndMinutes,
            summary: event.summary,
            backgroundColor,
            column: columnInfo?.column ?? 0,
            columnCount: columnInfo?.columnCount ?? 1,
            dragOffsetMinutes: dragOffsetMinutes ?? 0,
          },
          isDragging: true,
          dragPreview: null,
        });
      },

      updateDragPreview: (date, startMinutes, endMinutes) => {
        set({
          dragPreview: { date, startMinutes, endMinutes },
        });
      },

      clearDragPreview: () => {
        set({ dragPreview: null });
      },

      endEventDrag: () => {
        set({
          draggedEvent: null,
          dragPreview: null,
          isDragging: false,
        });
      },

      commitEventDrag: async () => {
        const { draggedEvent, dragPreview } = get();
        if (!draggedEvent || !dragPreview) {
          get().endEventDrag();
          return false;
        }

        const timezone = useCalendarStore.getState().timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

        let newStart: CalendarDateTimeValue;
        let newEnd: CalendarDateTimeValue;

        if (draggedEvent.allDay) {
          // All-day event - just update dates, preserve duration
          const startDate = dragPreview.date;
          const durationDays = Math.ceil(draggedEvent.durationMinutes / (24 * 60));
          const endDateObj = addDays(parseDateOnly(startDate)!, durationDays);
          const endDate = format(endDateObj, 'yyyy-MM-dd');

          newStart = { date: startDate, dateTime: null, timeZone: null };
          newEnd = { date: endDate, dateTime: null, timeZone: null };
        } else if (dragPreview.startMinutes === null) {
          // Timed event dropped on day-only zone (month/agenda view)
          // Preserve original times, just change the date
          const startMinutes = draggedEvent.originalStartMinutes ?? 9 * 60;
          const endMinutes = draggedEvent.originalEndMinutes ?? startMinutes + 60;

          // Calculate if event spans multiple days based on original duration
          const durationMinutes = endMinutes - startMinutes;

          const startDateTime = buildDateTime(dragPreview.date, startMinutes);
          let endDateTime: string;

          if (durationMinutes <= 0) {
            // Edge case: end is on next day from original
            // Calculate original day span
            const originalStartDate = draggedEvent.originalStart?.dateTime?.slice(0, 10);
            const originalEndDate = draggedEvent.originalEnd?.dateTime?.slice(0, 10);
            if (originalStartDate && originalEndDate && originalStartDate !== originalEndDate) {
              // Multi-day timed event - preserve the day offset
              const dayOffset = Math.round((new Date(originalEndDate).getTime() - new Date(originalStartDate).getTime()) / (24 * 60 * 60 * 1000));
              const endDateObj = addDays(parseDateOnly(dragPreview.date)!, dayOffset);
              endDateTime = buildDateTime(format(endDateObj, 'yyyy-MM-dd'), draggedEvent.originalEndMinutes ?? endMinutes);
            } else {
              endDateTime = buildDateTime(dragPreview.date, startMinutes + draggedEvent.durationMinutes);
            }
          } else {
            endDateTime = buildDateTime(dragPreview.date, endMinutes);
          }

          newStart = { date: null, dateTime: startDateTime, timeZone: timezone };
          newEnd = { date: null, dateTime: endDateTime, timeZone: timezone };
        } else {
          // Timed event with specific times (timeline view)
          const startMinutes = dragPreview.startMinutes;
          const endMinutes = dragPreview.endMinutes ?? startMinutes + draggedEvent.durationMinutes;

          const startDateTime = buildDateTime(dragPreview.date, startMinutes);
          let endDateTime: string;

          if (endMinutes > 24 * 60) {
            // Event spans past midnight - calculate next day
            const overflowMinutes = endMinutes - 24 * 60;
            const nextDay = addDays(parseDateOnly(dragPreview.date)!, 1);
            endDateTime = buildDateTime(format(nextDay, 'yyyy-MM-dd'), overflowMinutes);
          } else {
            endDateTime = buildDateTime(dragPreview.date, endMinutes);
          }

          newStart = { date: null, dateTime: startDateTime, timeZone: timezone };
          newEnd = { date: null, dateTime: endDateTime, timeZone: timezone };
        }

        // Check if position actually changed - skip API call if unchanged
        const startUnchanged = isSameDateTimeValue(draggedEvent.originalStart, newStart);
        const endUnchanged = isSameDateTimeValue(draggedEvent.originalEnd, newEnd);

        get().endEventDrag();

        if (startUnchanged && endUnchanged) {
          // No change - skip API call
          return true;
        }

        // Get the original event for optimistic update
        const originalEvent = useCalendarStore.getState().eventsById[draggedEvent.eventId];
        if (!originalEvent) {
          return false;
        }

        // Optimistically update the event in the store
        const optimisticEvent: CalendarEventModel = {
          ...originalEvent,
          start: newStart,
          end: newEnd,
          allDay: draggedEvent.allDay,
        };
        useCalendarStore.getState().upsertEvents([optimisticEvent]);

        try {
          const result = await useCalendarActionsStore.getState().updateEvent({
            calendarUid: draggedEvent.calendarUid,
            eventId: draggedEvent.eventId,
            event: {
              start: newStart,
              end: newEnd,
              allDay: draggedEvent.allDay,
            },
            sendUpdates: 'none',
          });
          if (!result) {
            // API failed - revert to original
            useCalendarStore.getState().upsertEvents([originalEvent]);
            return false;
          }
          return true;
        } catch {
          // Revert on error
          useCalendarStore.getState().upsertEvents([originalEvent]);
          return false;
        }
      },

      // Resize actions
      startEventResize: (event, edge, date, columnInfo, rowIndex) => {
        const startMinutes = getMinutesFromDateTime(event.start?.dateTime);
        const endMinutes = getMinutesFromDateTime(event.end?.dateTime);
        const originalStartDate = event.start?.date ?? event.start?.dateTime?.slice(0, 10) ?? null;
        const originalEndDate = event.end?.date ?? event.end?.dateTime?.slice(0, 10) ?? null;

        set({
          resizeEvent: {
            eventId: event.id,
            calendarUid: event.calendarUid,
            edge,
            originalStart: event.start,
            originalEnd: event.end,
            originalStartMinutes: startMinutes,
            originalEndMinutes: endMinutes,
            date,
            allDay: event.allDay,
            originalStartDate,
            originalEndDate,
            column: columnInfo?.column ?? 0,
            columnCount: columnInfo?.columnCount ?? 1,
            rowIndex: rowIndex ?? 0,
          },
          resizePreview: {
            startMinutes,
            endMinutes,
            startDate: originalStartDate,
            endDate: originalEndDate,
          },
          isResizing: true,
        });
      },

      updateResizePreview: (preview) => {
        const { resizeEvent, resizePreview: currentPreview } = get();
        if (!resizeEvent || !currentPreview) return;

        if (resizeEvent.allDay) {
          // All-day event resizing - update dates
          const newStartDate = preview.startDate ?? currentPreview.startDate;
          const newEndDate = preview.endDate ?? currentPreview.endDate;

          // Ensure start is before or equal to end using proper date comparison
          if (newStartDate && newEndDate) {
            const startParsed = parseDateOnly(newStartDate);
            const endParsed = parseDateOnly(newEndDate);
            if (startParsed && endParsed && startParsed > endParsed) {
              return; // Invalid state, don't update
            }
          }

          set({
            resizePreview: {
              ...currentPreview,
              startDate: newStartDate,
              endDate: newEndDate,
            },
          });
        } else {
          // Timed event resizing - update minutes
          const startMinutes = preview.startMinutes ?? currentPreview.startMinutes;
          const endMinutes = preview.endMinutes ?? currentPreview.endMinutes;

          // Enforce minimum duration of 15 minutes
          const minDuration = 15;
          let adjustedStart = startMinutes;
          let adjustedEnd = endMinutes;

          if (resizeEvent.edge === 'start') {
            // Dragging start edge - don't let start go past end - minDuration
            adjustedStart = Math.min(startMinutes, endMinutes - minDuration);
            adjustedStart = Math.max(0, adjustedStart); // Don't go before midnight
          } else {
            // Dragging end edge - don't let end go before start + minDuration
            adjustedEnd = Math.max(endMinutes, startMinutes + minDuration);
            adjustedEnd = Math.min(24 * 60, adjustedEnd); // Don't go past midnight
          }

          set({
            resizePreview: {
              ...currentPreview,
              startMinutes: adjustedStart,
              endMinutes: adjustedEnd,
            },
          });
        }
      },

      endEventResize: () => {
        set({
          resizeEvent: null,
          resizePreview: null,
          isResizing: false,
        });
      },

      commitEventResize: async () => {
        const { resizeEvent, resizePreview } = get();
        if (!resizeEvent || !resizePreview) {
          get().endEventResize();
          return false;
        }

        const timezone = useCalendarStore.getState().timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

        let newStart: CalendarDateTimeValue;
        let newEnd: CalendarDateTimeValue;

        if (resizeEvent.allDay) {
          // All-day event - update dates
          // End date is exclusive for all-day events, so add 1 day
          const endDateObj = resizePreview.endDate ? addDays(parseDateOnly(resizePreview.endDate)!, 1) : null;
          const endDate = endDateObj ? format(endDateObj, 'yyyy-MM-dd') : resizePreview.endDate;

          newStart = { date: resizePreview.startDate, dateTime: null, timeZone: null };
          newEnd = { date: endDate, dateTime: null, timeZone: null };
        } else {
          // Timed event - update times
          // Use original dates for multi-day events: adjust only the edge being resized
          const startDate = resizeEvent.originalStartDate ?? resizeEvent.date;
          const endDate = resizeEvent.originalEndDate ?? resizeEvent.date;

          let startDateTime: string;
          let endDateTime: string;

          if (resizeEvent.edge === 'start') {
            // Resizing start - use new start time, preserve original end
            startDateTime = buildDateTime(startDate, resizePreview.startMinutes);
            endDateTime = buildDateTime(endDate, resizeEvent.originalEndMinutes);
          } else {
            // Resizing end - preserve original start, use new end time
            startDateTime = buildDateTime(startDate, resizeEvent.originalStartMinutes);
            endDateTime = buildDateTime(endDate, resizePreview.endMinutes);
          }

          newStart = { date: null, dateTime: startDateTime, timeZone: timezone };
          newEnd = { date: null, dateTime: endDateTime, timeZone: timezone };
        }

        // Check if size actually changed - skip API call if unchanged
        const startUnchanged = isSameDateTimeValue(resizeEvent.originalStart, newStart);
        const endUnchanged = isSameDateTimeValue(resizeEvent.originalEnd, newEnd);

        get().endEventResize();

        if (startUnchanged && endUnchanged) {
          // No change - skip API call
          return true;
        }

        // Get the original event for optimistic update
        const originalEvent = useCalendarStore.getState().eventsById[resizeEvent.eventId];
        if (!originalEvent) {
          return false;
        }

        // Optimistically update the event in the store
        const optimisticEvent: CalendarEventModel = {
          ...originalEvent,
          start: newStart,
          end: newEnd,
          allDay: resizeEvent.allDay,
        };
        useCalendarStore.getState().upsertEvents([optimisticEvent]);

        try {
          const result = await useCalendarActionsStore.getState().updateEvent({
            calendarUid: resizeEvent.calendarUid,
            eventId: resizeEvent.eventId,
            event: {
              start: newStart,
              end: newEnd,
              allDay: resizeEvent.allDay,
            },
            sendUpdates: 'none',
          });
          if (!result) {
            // API failed - revert to original
            useCalendarStore.getState().upsertEvents([originalEvent]);
            return false;
          }
          return true;
        } catch {
          // Revert on error
          useCalendarStore.getState().upsertEvents([originalEvent]);
          return false;
        }
      },

      // Quick-create actions
      openQuickCreate: (date, startMinutes, endMinutes, anchorPosition) => {
        set({
          quickCreate: {
            open: true,
            date,
            startMinutes,
            endMinutes,
            anchorPosition,
          },
        });
      },

      closeQuickCreate: () => {
        set({
          quickCreate: { ...INITIAL_QUICK_CREATE },
        });
      },
    }),
    { name: 'CalendarDragDropStore' }
  )
);
